/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ElementRef, Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormActionPipelines, FormFlowState } from '@gh/builder-shared';
import { GraphicalTakeOffPoint } from '@gh/builder-shared-data';
import { ActionFlow, getDialogInput, isDirty, streamValueAction } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { MessageBasket, MessageSeverity } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  confirmationWith,
  DialogService,
  FormMode,
  NotificationService,
  UploadContext,
  UploadItem,
  WaitingService,
  yesAnswer,
  yesCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { denull, isNotNil } from '@gh/core-util';
import { combineLatest$, defer$, EMPTY$, from$, fromEvent$, merge$, of$ } from '@gh/rx/index';
import {
  concatAll$,
  distinctUntilChanged$,
  filter$,
  finalize$,
  last$,
  map$,
  mapTo$,
  switchMap$,
  takeUntil$,
  tap$,
} from '@gh/rx/operators';
import {
  clamp,
  find,
  findLast,
  first,
  flatMap,
  flatten,
  groupBy,
  initial,
  isEmpty,
  isNil,
  last,
  mapValues,
  min,
  negate,
  toLower,
  toPairs,
  trim,
  uniqueId,
} from 'lodash';
import { Item, Layer, MouseEvent, PaperScope, Point, Raster, Shape, Tool } from 'paper';
import { Observable, Subject, Subscriber } from 'rxjs';
import { createVirtualRaster, DEFAULT_DPI } from './components/canvas/canvas-rasterize.utils';
import {
  canvasToBlob,
  cleanZLevels,
  fromPaperPoint,
  getClosestOfType,
  getClosestWithType,
  getItemType,
  isPointInBox,
  setupZLevels,
  toPaperPoint,
} from './components/canvas/canvas.utils';
import { graphicalTakeOffEditDialogRoute } from './components/edit';
import { GraphicalTakeOffImportComponent } from './components/import';
import { GraphicalTakeOffMainConfig } from './components/main';
import { graphicalTakeOffZoomDialogRoute } from './components/zoom';
import { GraphicalTakeOffFormState } from './graphical-take-off-form.service';
import {
  addPoint,
  calculateMinDistanceToPath,
  canDrawMeasurement,
  diffPoints,
  findClosestPoint,
  findClosestPointOnDistance,
  getGraphicalTakeOffHousePlanViewId,
  getGraphicalTakeOffMeasurementId,
  getGraphicalTakeOffTopType,
  getGraphicalTakeOffTopViewId,
  getGraphicalTakeOffTopViewLastMeasurement,
  getGraphicalTakeOffViewImage,
  getGraphicalTakeOffViewImagePipeline,
  getGraphicalTakeOffViewImageSize,
  getGtoShortcutParentScope,
  getPathIntersectionCount,
  GraphicalTakeOffBox,
  GraphicalTakeOffBusinessService,
  GraphicalTakeOffChangeOption,
  GraphicalTakeOffDataBoxVisibility,
  GraphicalTakeOffDetails,
  graphicalTakeOffDetailsToHousePlanSaveWithImage,
  GraphicalTakeOffHousePlanView,
  GraphicalTakeOffImageTransform,
  GraphicalTakeOffMeasurement,
  GraphicalTakeOffScaleDirection,
  GraphicalTakeOffSize,
  GraphicalTakeOffTop,
  GraphicalTakeOffTopView,
  GraphicalTakeOffViewInfo,
  GTO_IMAGE_PADDING_RATIO,
  GTO_MIN_MEASUREMENT_POINT_DISTANCE,
  GTO_SNAP_GRID_MAX_DISTANCE,
  GtoShortcutScope,
  GtoToolName,
  isGraphicalTakeOffAreaType,
  isGraphicalTakeOffCountType,
  isGraphicalTakeOffImageTransformed,
  isGraphicalTakeOffMeasurementClosed,
  isGtoValidHorizontalScaleLine,
  isGtoValidVerticalScaleLine,
  isValidToClone,
  maxPoint,
  minPoint,
  newPoint,
  newSize,
  pointLength,
  UserGTOPreferences,
  ZOOM_VALUES,
} from './resources';

// unfortunately we cannot import 'kdbush' because it imports ES6 code which does not work in IE
// tslint:disable-next-line
const KDBush = require('kdbush/kdbush');

export interface PaperEventEmitter {
  on(type: string, callback: (event: Event) => void): this;

  off(type: string, callback: (event: Event) => void): this;
}

export interface ViewportManager {
  getScrollLeft(): number;

  getScrollTop(): number;

  setScrollTop(top: number): void;

  setScrollLeft(left: number): void;

  setScrollWidth(width: number): void;

  setScrollHeight(height: number): void;
}

interface GtoShortcutDef {
  on: 'down' | 'up';
  when: PredicateFn<KeyboardEvent>;
  run: ProcedureFn<KeyboardEvent>;
}

interface GtoShortcutKey {
  key: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

type GtoShortcutDefMap = Hash<Hash<GtoShortcutDef[]>>;

const FUNCTION_KEYS = {
  ctrl: true,
  shift: true,
  alt: true,
};

const isFunctionKey = (key: string) => FUNCTION_KEYS[key] || false;

const parseKeyExpression = (expression: string): GtoShortcutKey => {
  const keyCodes = expression.split('+').map(trim).map(toLower);
  const key = find(keyCodes, negate(isFunctionKey));
  if (isNil(key)) {
    throw new Error(`Cannot parse short: ${key}`);
  }
  return {
    key,
    ctrlKey: keyCodes.includes('ctrl'),
    shiftKey: keyCodes.includes('shift'),
    altKey: keyCodes.includes('alt'),
  };
};

const isEventBelongsForKey = (event: KeyboardEvent, key: GtoShortcutKey) =>
  (key.ctrlKey ? event.ctrlKey : true)
  && (key.shiftKey ? event.shiftKey : true)
  && (key.shiftKey ? event.shiftKey : true);

const normalizeShortcutMap = (map: GtoShortcutDefMap): GtoShortcutDefMap => mapValues(map, (scopeMap) => {
    const parsedKeyAndDefTuples = flatten(toPairs(scopeMap)
      .map(([keyExpressions, defs]) => <any>[keyExpressions.split(',').map(parseKeyExpression), defs])
      .map(([parsedKeys, defs]) => parsedKeys.map((parsedKey: GtoShortcutKey) => [parsedKey, defs])));
    return mapValues(
      groupBy(parsedKeyAndDefTuples, ([parsedKey]) => parsedKey.key),
      (tuples) => flatMap(tuples, ([parsedKey, defs]: any) => defs.map((def: any) => ({
        ...def,
        when: (event: KeyboardEvent) => isEventBelongsForKey(event, parsedKey) ? def.when(event) : false,
      }))));
  });

const fromPaperEvent$ = (emitter: PaperEventEmitter, type: string) =>
  new Observable((subscriber) => {
    const onEvent = (event: any) => subscriber.next(event);

    emitter.on(type, onEvent);

    return () => emitter.off(type, onEvent);
  });

class FocusManager {
  private scopeToElement: Hash<HTMLElement> = {};

  constructor(rootEl: HTMLElement) {
    rootEl.querySelectorAll<HTMLElement>('[data-shortcut-scope]').forEach((scopeEl) => {
      const scopeName = scopeEl.dataset['shortcutScope'];
      if (scopeName) {
        this.scopeToElement[scopeName] = scopeEl;
      }
    });
  }

  focus(scope: GtoShortcutScope): void {
    const scopeEl = this.scopeToElement[scope];
    if (scopeEl) {
      scopeEl.focus();
    }
  }
}

@Injectable()
export class GraphicalTakeOffCommandService implements OnDestroy {
  private input: GraphicalTakeOffMainConfig;
  private scope: PaperScope;
  private cornerIndex: any;
  private snapGrid: Item[] = [];
  private destroy$$ = new Subject<void>();
  private viewportManager: ViewportManager;
  private focusManager: FocusManager;
  private viewportDragMode = false;
  private drawingTool: Tool;
  private scalingTool: Tool;
  private cropTool: Tool;
  private noopTool: Tool;
  private editable: boolean;

  constructor(private mainElementRef: ElementRef,
              private business: GraphicalTakeOffBusinessService,
              private translations: TranslationService,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private form: GraphicalTakeOffFormState,
              private ngZone: NgZone,
              private waitings: WaitingService,
              private uploadContext: UploadContext,
              private pipelines: FormActionPipelines,
              private dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any) {
    this.input = getDialogInput<GraphicalTakeOffMainConfig>(dialogData);
  }

  get completeFlow(): ActionFlow<FormFlowState<GraphicalTakeOffDetails>> {
    return this.pipelines.complete({
      execute: streamValueAction((state) =>
        this.validateCurrentTool().pipe(
          filter$(Boolean),
          switchMap$(() =>
            this.uploadAndSave(this.form.views.filter(isDirty)).pipe(this.waitings.operator())),
          mapTo$(state))),
      finish: () => this.build(this.editable, true),
      error: ({ error }) => {
        if (error) {
          this.handleSaveError(error);
        }
      },
    });
  }

  ngOnDestroy(): void {
    cleanZLevels();
    this.destroy$$.next();
  }

  getRasterLayer(): Layer {
    return <Layer>this.scope.project.getItem(({ data }: any) => data.id === 'rasterLayer');
  }

  getRaster(): Item {
    return this.getRasterLayer()['$raster'];
  }

  getCanvasElement(): HTMLCanvasElement {
    return this.scope.view.element;
  }

  build(editable: boolean, isAfterSave?: boolean): void {
    if (editable === true || editable === false) {
      this.editable = editable ;
    }
    this.form.setMode(this.editable ? FormMode.EditMode : FormMode.ViewMode);
    this.form.resetDirty();
    this.loadAllViews(isAfterSave);

    this.setupShortcuts();
  }

  initViewportManager(scrollContainerManager: ViewportManager): void {
    this.viewportManager = scrollContainerManager;
  }

  initCanvas(scope: PaperScope): void {
    this.scope = scope;

    this.focusManager = new FocusManager(this.mainElementRef.nativeElement);

    this.updateViewportSize();

    scope.view.off('mousedown');
    scope.view.off('mouseup');
    scope.view.off('mousedrag');
    scope.view.off('mousemove');
    scope.view.off('doubleclick');

    new Layer({
      data: { id: 'mainLayer', type: 'main-layer' },
    });

    cleanZLevels();
    setupZLevels(['background', 'filling', 'measurement', 'selection', 'databox', 'highlight', 'tool']);

    this.initTools();

    let cursorBeforeViewportDragMode = 'auto';
    let viewportDragStartScrollPosition = newPoint(0, 0);
    let viewportDragStartPoint = newPoint(0, 0);
    let isDoubleClickAllowed = false;

    fromPaperEvent$(scope.view, 'mousedown').pipe(
      tap$((event: MouseEvent) => {
        isDoubleClickAllowed = true;

        const button = event.event.button;
        if (button === 1) {
          // when user presses middle button => start viewport drag mode
          this.viewportDragMode = true;
          // save initial cursor to restore it after viewport drag mode will be completed
          cursorBeforeViewportDragMode = this.getCanvasCursor();
          this.setCanvasCursor('grabbing');
          viewportDragStartScrollPosition = newPoint(
            this.viewportManager.getScrollLeft(),
            this.viewportManager.getScrollTop());
          viewportDragStartPoint = newPoint(event.event.offsetX, event.event.offsetY);
        }
      }),
      switchMap$(() =>
        fromPaperEvent$(scope.view, 'mousedrag').pipe(
          takeUntil$(fromPaperEvent$(scope.view, 'mouseup')),
          finalize$(() => {
            if (this.viewportDragMode) {
              this.viewportDragMode = false;
              this.setCanvasCursor(cursorBeforeViewportDragMode);
            }
          }))))
      .subscribe((event: MouseEvent) => {
        if (this.viewportDragMode) {
          const nextScrollPosition = addPoint(
            viewportDragStartScrollPosition,
            diffPoints(viewportDragStartPoint, newPoint(event.event.offsetX, event.event.offsetY)));
          this.setViewportPosition(nextScrollPosition.x, nextScrollPosition.y);
          // to guarantee that we are in allowed viewport bounds => adjust viewport position
          this.adjustViewportPosition();
          return;
        }

        if (this.form.viewMode) {
          return;
        }

        isDoubleClickAllowed = false;
      });

    this.ngZone.runOutsideAngular(() => {
      fromPaperEvent$(scope.view, 'mousemove').pipe(
        map$(() => {
          if (this.viewportDragMode) {
            // in viewport drag mode => always show grabbing cursor
            return 'grabbing';
          }
        }),
        filter$(Boolean),
        distinctUntilChanged$())
        .subscribe((cursor: any) => {
          if (cursor) {
            this.setCanvasCursor(cursor);
          }
        });
    });

    fromPaperEvent$(scope.view, 'doubleclick')
      .subscribe((event: MouseEvent) => {
        if (this.form.viewMode || !this.form.drawingMode || this.form.isInTopDrawingMode || event.event.button !== 0
          || !isDoubleClickAllowed) {
          return;
        }

        isDoubleClickAllowed = false;

        const result = scope.project.hitTest(event.point);
        if (result && result.item) {
          const dataBoxItem = getClosestOfType(result.item, 'databox');
          if (dataBoxItem) {
            const { topId, measurementId, dataBoxId } = dataBoxItem.data;
            this.openDataBox(topId, measurementId, dataBoxId);
          }
        }
      });
  }

  focus(scope: GtoShortcutScope): void {
    this.focusManager.focus(scope);
  }

  updateWindowSize(): void {
    const { currentView } = this.form;

    this.updateViewportSize();

    if (currentView && currentView.imageLoaded) {
      this.setZoom(this.form.zoom, false);
    }
  }

  currentViewImageTransform(transformation: GraphicalTakeOffImageTransform): void {
    this.form.currentViewImageTransform(transformation);
  }

  currentViewImageSetTransformation(transformation: GraphicalTakeOffImageTransform): void {
    this.form.currentViewImageSetTransformation(transformation);
  }

  currentViewResetTransformations(): void {
    this.form.currentViewResetTransformations();
  }

  startScaling(): void {
    this.form.setActiveTool(GtoToolName.SetScale);
    this.activateTool();

    if (this.form.isScaleInfoAccepted) {
      this.dialogs.message({
        messageId: 'msg_gto_set_scale_info',
        severity: MessageSeverity.Warning,
        panelClass: 'cdk-overlay-strict',
        btnColor: 'primary',
      }).subscribe(() => this.form.setScaleInfoAccepted());
    }
  }

  stopScaling(): void {
    this.resetActiveTool();
  }

  startCroping(): void {
    this.form.setActiveTool(GtoToolName.Crop);
    this.activateTool();
  }

  stopCroping(): void {
    this.resetActiveTool();
  }

  resetActiveTool(): void {
    this.form.resetActiveTool();
    this.activateTool();
  }

  applyScaling(): void {
    if (this.form.isCurrentViewScaled) {
      this.rescale();
    } else {
      this.saveScale();
    }
    this.resetActiveTool();
  }

  setCurrentTop(topId: ID): void {
    if (this.tryToFinalizeMeasurement()) {
      this.form.transaction(() => {
        this.form.setCurrentTop(topId);
        this.form.ensureOnlyVisibleTopSelected();
      });
    }
  }

  setViewportPosition(left: number, top: number): void {
    const point = this.getViewportCenterByScrollPosition(left, top);
    this.scope.view.center = toPaperPoint(point);

    if (this.viewportManager.getScrollLeft() !== left) {
      this.viewportManager.setScrollLeft(left);
    }
    if (this.viewportManager.getScrollTop() !== top) {
      this.viewportManager.setScrollTop(top);
    }

    this.form.setViewCenter(point);
  }

  zoomAroundViewPoint(left: number, top: number, zoomDirection: number): void {
    // when we zoom around some point, we have to remain this point on the same position in viewport
    const anchorPoint = this.scope.view.viewToProject(new Point(left, top));
    const { view } = this.scope;
    const currentZoom = view.zoom;
    const nextZoom = zoomDirection < 0 ?
      find(ZOOM_VALUES, (zoomLevel) => zoomLevel > currentZoom)
      : findLast(ZOOM_VALUES, (zoomLevel) => zoomLevel < currentZoom);
    if (nextZoom) {
      // to remain anchor point on the same position in viewport
      // we have to guarantee that distance from anchor point to the center is the same in viewport coordinates
      const toCenterVector = view.center.subtract(anchorPoint);
      const distanceToCenter = toCenterVector.multiply(currentZoom).length;
      view.center = anchorPoint.add(toCenterVector.normalize(distanceToCenter / nextZoom));
      this.setZoom(nextZoom);
    }
  }

  setZoom(zoom: number, centerPictureWhen0: boolean = true, viewCenter?: GraphicalTakeOffPoint): void {
    let normalizedZoom: number;

    const imageSize = this.getImageSize();
    const imageSiteSize = this.getImageSiteSize();

    // if zoom === 0 => we suppose that user requests fit to screen mode
    if (zoom === 0) {
      const viewportSize = this.getViewportSize();

      // calculate "best" zoom factor
      // tslint:disable-next-line:no-non-null-assertion
      normalizedZoom = min([1, viewportSize.width / imageSize.width, viewportSize.height / imageSize.height])!;
    } else {
      normalizedZoom = zoom;
    }
    this.form.setZoom(zoom, 1 / normalizedZoom);
    this.scope.view.zoom = normalizedZoom;

    // recalculate viewport dimensions and position
    this.viewportManager.setScrollWidth(imageSiteSize.width * normalizedZoom);
    this.viewportManager.setScrollHeight(imageSiteSize.height * normalizedZoom);

    // when fit to screen is requested we can center image
    if (viewCenter) {
      this.scope.view.center = toPaperPoint(viewCenter);
    } else if (zoom === 0 && centerPictureWhen0) {
      // tslint:disable-next-line:no-magic-numbers
      this.scope.view.center = new Point(imageSize.width / 2, imageSize.height / 2);
    }

    this.adjustViewportPosition();
  }

  setOverlay(isOverlay: boolean): void {
    this.form.setOverlay(isOverlay);
    this.form.ensureOnlyVisibleTopSelected();
  }

  updateUserPreference(userPreference: UserGTOPreferences): void {
    const userGTOPreference = sessionStorage.getItem("UserGTOPreferences");
    let currentPreferences: UserGTOPreferences = userGTOPreference ? JSON.parse(userGTOPreference) : {};
    const updatedPreferences = {
      ...currentPreferences,
      ...userPreference
    };
    sessionStorage.setItem("UserGTOPreferences", JSON.stringify(updatedPreferences));
  }

  setDataBoxVisibility(visibility: GraphicalTakeOffDataBoxVisibility): void {
    this.form.setDataBoxVisibility(visibility);
  }

  deleteSelectedMeasurement(): void {
    this.form.transaction(() => {
      if (this.tryToFinalizeMeasurement()) {
        this.form.deleteSelectedMeasurement();
      }
    });
  }

  addTop(top: GraphicalTakeOffTop): void {
    this.form.addTop(top);
  }

  moveTopTo(top: GraphicalTakeOffTop, option: GraphicalTakeOffChangeOption): void {
    this.form.transaction(() => {
      this.form.moveTopTo(top, option);
    });
  }

  setViewScale(scaleH?: number, scaleV?: number): void {
    this.form.setCurrentViewScale(scaleH, scaleV);
  }

  removeView(viewId: ID): void {
    this.form.removeView(viewId);
  }

  tryToFinalizeMeasurement(): boolean {
    return this.form.transaction(() => {
      if (!this.validateCurrentTop()) {
        return false;
      }
      this.completeMeasurement();
      return true;
    });
  }

  snapToStart(): void {
    this.form.transaction(() => {
      this.form.snapToStart();
    });
  }

  switchOnTopAppendMode(): void {
    if (this.tryToFinalizeMeasurement()) {
      this.form.switchOnTopAppendMode();
    }
  }

  setSnapToPoint(snapToPoint: boolean): void {
    this.form.setSnapToPoint(snapToPoint);
  }

  cloneTop(sourceTop: GraphicalTakeOffTopView,
           measurement: GraphicalTakeOffMeasurement,
           targetTop: GraphicalTakeOffTop): void {
    if (!isValidToClone(measurement, targetTop)) {
      this.dialogs.error('msg_gto_area_line_crossed');
      return;
    }

    this.form.transaction(() => {
      this.form.cloneTop(sourceTop, measurement, targetTop);
    });
  }

  requestView(viewId: ID): void {
    const previousSelectedViewId = this.form.selectedViewId;
    this.form.setSelectedViewId(viewId);

    this.validateCurrentTool()
      .subscribe((valid) => {
        if (valid) {
          this.openView(viewId);
        } else {
          this.form.setSelectedViewId(previousSelectedViewId);
        }
      });
  }

  openView(viewId: ID, currentValue?: any): void {
    const view = this.form.getView(viewId);

    if (isNil(view)) {
      return;
    }

    const loadImage$ = (src: string) => new Observable((subscriber: Subscriber<HTMLImageElement>) => {
      const image = new Image();
      image.setAttribute('crossOrigin', 'anonymous');
      image.onload = () => {
        subscriber.next(image);
      };
      image.onerror = () => {
        subscriber.error(MessageBasket.error('msg_internalservererror'));
      };

      image.src = src;
    });

    const getUrlAndLoadImage$ = this.business.getPicture(view.picture.key).pipe(
      switchMap$(({ presignedURl }) => presignedURl ? loadImage$(presignedURl) : of$(void 0)),
      tap$((image) => this.form.setViewImage(viewId, image!))); // tslint:disable-line:no-non-null-assertion

    const image$ = view.imageLoaded ? of$(view.image) : getUrlAndLoadImage$;

    const loadViewSnapGrid$ = this.business.getViewSnapGrid(this.input.id, viewId, this.input.entryPoint).pipe(
      tap$((corners) => this.form.setViewSnapGrid(viewId, corners)));
    const getViewSnapPoints$ = view.snapGridLoaded ? of$(view.corners) : loadViewSnapGrid$;
    const snapPoints$ = view.isScaled ? getViewSnapPoints$ : of$([]);

    combineLatest$([image$, snapPoints$])
      .pipe(this.waitings.operator())
      .subscribe(
        () => {
          this.setCurrentViewId(viewId);
          this.migrateTopViews(viewId);
          this.activateTool();
          if (currentValue) {
            this.form.setSelectedViewId(currentValue.selectedView);
            this.form.setOverlay(currentValue.isOverlay);
            this.form.setSnapToPoint(currentValue.isSnapToPoint);
            this.setZoom(currentValue.zoom);
          }
        },
        this.notifications.processAll);
  }

  import(): void {
    if (!this.tryToFinalizeMeasurement()) {
      return;
    }

    this.validateCurrentTool().pipe(
      filter$(Boolean),
      switchMap$(() =>
        this.dialogs.open(GraphicalTakeOffImportComponent, {
          width: '600px',
          disableClose: true,
          data: {
            views: this.form.views,
            id: this.input.id,
            entryPoint: this.input.entryPoint,
            gFolderId: this.input.gFolderId,
          },
        }).afterClosed()))
      .subscribe((newViews: GraphicalTakeOffHousePlanView[]) => {
        this.focus(GtoShortcutScope.Canvas);

        if (!newViews || newViews.length === 0) {
          if (this.form.views.length === 0) {
            this.dialogRef.close({ success: true });
          }
        } else {
          this.loadNewViews(newViews);
        }
      });
  }

  undo(): void {
    const conflicts = this.form.getUndoConflicts();
    if (!isEmpty(conflicts)) {
      this.notifications.error(
        'msg_gto_undo_not_possible',
        {
          views: conflicts
            .map((viewId) => this.form.getView(viewId)!.name) // tslint:disable-line:no-non-null-assertion
            .join(', '),
        });
      return;
    }

    this.form.undo();
  }

  redo(): void {
    const conflicts = this.form.getRedoConflicts();
    if (!isEmpty(conflicts)) {
      this.notifications.error(
        'msg_gto_redo_not_possible',
        {
          views: conflicts
            .map((viewId) => this.form.getView(viewId)!.name) // tslint:disable-line:no-non-null-assertion
            .join(', '),
        });
      return;
    }

    this.form.redo();
  }

  setScaleLineDirection(direction: GraphicalTakeOffScaleDirection): void {
    this.form.setScaleLineDirection(direction);
    this.form.resetScaleLine();
    this.form.setScaleLineWaitingForLength(false);
  }

  setScaleLineLength(length: number): void {
    this.form.setScaleLineLength(length);

    this.form.resetScaleLine();
    if (!this.form.isScaleCompleted) {
      const currentDirection = this.form.currentScaleLineDirection;
      this.form.setScaleLineDirection(currentDirection === GraphicalTakeOffScaleDirection.Vertical ?
        GraphicalTakeOffScaleDirection.Horizontal
        : GraphicalTakeOffScaleDirection.Vertical);
    } else {
      this.form.setScaleLineDirection(GraphicalTakeOffScaleDirection.Horizontal);
    }
  }

  autoSave(): Observable<any> {
    if (!this.form.dirty) {
      return EMPTY$;
    }

    return this.dialogs.confirmation(yesNoConfirmation('msg_unsaved_info')).pipe(
      filter$(yesAnswer),
      switchMap$(() => {
        const flow = this.completeFlow;

        return flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form }).toFinalState();
      }));
  }

  private highlightDataBox(topId: ID, measurementId: ID, dataBoxId: ID): void {
    this.form.highlightDataBox(topId, measurementId, dataBoxId);
  }

  private unhighlightDataBox(): void {
    this.form.unhighlightDataBox();
  }

  private moveSelectedDataBox(point: GraphicalTakeOffPoint): void {
    this.form.pendingTransaction(() => {
      const { highlightedTopId, highlightedMeasurementId, highlightedDataBoxId } = this.form;
      if (highlightedTopId && highlightedMeasurementId && highlightedDataBoxId) {
        const topView = this.form.getTopView(highlightedTopId)!; // tslint:disable-line:no-non-null-assertion
        const type = getGraphicalTakeOffTopType(topView);
        // for COUNT ToP => we move ToP position along with data box
        const normalizedPoint = isGraphicalTakeOffCountType(type) ? this.normalizePoint(point) : point;
        this.form.setDataBoxPosition(highlightedTopId, highlightedMeasurementId, highlightedDataBoxId, normalizedPoint);
      }
    });
  }

  private openDataBox(topId: ID, measurementId: ID, dataBoxId: ID): void {
    const top = this.form.getTopView(topId);
    const dataBox = this.form.getDataBox(topId, measurementId, dataBoxId);

    if (top && dataBox) {
      this.dialogs.openWithInput(graphicalTakeOffEditDialogRoute, {
        top,
        dataBox,
      }).afterClosed()
        .pipe(
          tap$(() => this.focus(GtoShortcutScope.Canvas)),
          filter$(Boolean))
        .subscribe((updatedDataBox: GraphicalTakeOffBox) => {

          this.form.transaction(() => {
            this.form.setDataBox(topId, measurementId, updatedDataBox);
          });
        });
    }
  }

  private beginMeasurementPoint(point: GraphicalTakeOffPoint): void {
    const { currentTopView } = this.form;

    if (isNotNil(currentTopView) && !canDrawMeasurement(currentTopView)) {
      this.switchOnTopAppendMode();
    }

    const { drawingMeasurement } = this.form;

    const firstMeasurementPoint = drawingMeasurement ? first(drawingMeasurement.points) : void 0;
    const normalizedPoint = this.normalizePoint(point, firstMeasurementPoint ? [firstMeasurementPoint] : void 0);

    this.form.beginMeasurementPoint(normalizedPoint);
  }

  private setMeasurementPoint(point: GraphicalTakeOffPoint): void {
    this.form.transaction(() => {
      this.beginMeasurementPoint(point);
      this.finalizeMeasurementPoint();
    });
  }

  private setMeasurementPointPosition(point: GraphicalTakeOffPoint): void {
    const { drawingMeasurement } = this.form;

    const firstMeasurementPoint = drawingMeasurement ? first(drawingMeasurement.points) : void 0;
    const normalizedPoint = this.normalizePoint(point, firstMeasurementPoint ? [firstMeasurementPoint] : void 0);

    this.form.setMeasurementPointPosition(normalizedPoint);
  }

  private finalizeMeasurementPoint(): void {
    const { drawingTop, drawingMeasurement } = this.form;

    let isMeasurementClosed = false;
    if (drawingTop && drawingMeasurement) {
      const { points } = drawingMeasurement;
      const lastPoint = last(points)!; // tslint:disable-line:no-non-null-assertion
      const pointsButLast = initial(points);

      isMeasurementClosed = isGraphicalTakeOffMeasurementClosed(drawingMeasurement);

      const type = getGraphicalTakeOffTopType(drawingTop);

      // tslint:disable-next-line:no-magic-numbers
      if (isGraphicalTakeOffAreaType(type) && points.length > 2) {
        // tslint:disable-next-line:no-magic-numbers
        const intersectionCount = getPathIntersectionCount(pointsButLast, [points[points.length - 2], lastPoint]);
        // tslint:disable-next-line:no-magic-numbers
        if (isMeasurementClosed ? intersectionCount > 2 : intersectionCount > 1) {
          this.dialogs.error('msg_gto_area_line_crossed');
          this.form.rollbackMeasurementPoint();
          return;
        }
      }

      const minPointDistance = GTO_MIN_MEASUREMENT_POINT_DISTANCE * this.form.scale;
      const minDistanceToPath = calculateMinDistanceToPath(pointsButLast, lastPoint);
      // tslint:disable-next-line:no-magic-numbers
      if (!isMeasurementClosed && minDistanceToPath <= minPointDistance || isMeasurementClosed && points.length === 2) {
        this.dialogs.error('msg_gto_cannot_reselect_same_point');
        this.form.rollbackMeasurementPoint();
        return;
      }
    }

    this.form.transaction(() => {
      this.form.commitMeasurementPoint();

      if (isMeasurementClosed) {
        this.completeMeasurement();
        this.form.selectMeasurement(
          getGraphicalTakeOffTopViewId(drawingTop!), // tslint:disable-line:no-non-null-assertion
          getGraphicalTakeOffMeasurementId(drawingMeasurement!)); // tslint:disable-line:no-non-null-assertion
      } else {
        const { currentTopView } = this.form;

        if (currentTopView && isGraphicalTakeOffCountType(getGraphicalTakeOffTopType(currentTopView))) {
          const lastDrawnMeasurement = getGraphicalTakeOffTopViewLastMeasurement(currentTopView);
          if (lastDrawnMeasurement) {
            this.form.selectMeasurement(
              getGraphicalTakeOffTopViewId(currentTopView!), // tslint:disable-line:no-non-null-assertion
              getGraphicalTakeOffMeasurementId(lastDrawnMeasurement)); // tslint:disable-line:no-non-null-assertion
          }
        }
      }
    });
  }

  private validateCurrentTop(): boolean {
    if (this.form.isInTopDrawingMode && !this.form.isDrawingMeasurementValid) {
      this.dialogs.error('msg_gto_incomplete_measurement');
      return false;
    }

    return true;
  }

  private validateCurrentTool(): Observable<boolean> {
    if (this.form.isSetScaleToolActive) {
      return this.dialogs.confirmation(confirmationWith(
        yesCancelConfirmation('msg_gto_set_scale_will_be_lost'),
        { yesLabel: this.translations.get('lbl_continue') }))
        .pipe(map$(yesAnswer));
    } else {
      return of$(true);
    }
  }

  private completeMeasurement(): void {
    if (this.form.isDrawingMeasurementValid) {
      this.form.completeMeasurement();
    }
  }

  private uploadAndSave(views: GraphicalTakeOffHousePlanView[]): Observable<GraphicalTakeOffHousePlanView> {
    type GtoViewSaveModel = { view: GraphicalTakeOffHousePlanView; uploadItem?: UploadItem };

    if (!this.tryToFinalizeMeasurement()) {
      return EMPTY$;
    }

    const upload = (view: GraphicalTakeOffHousePlanView) =>
      this.rasterize(view).pipe(
        switchMap$((blob) =>
          this.uploadContext.upload(
            {
              name: view.name,
              mimeType: blob.type,
              uploadId: uniqueId('upload-item:'),
              blob: new File([blob], view.name),
            },
            { entryPoint: 'TEMPORARY' })));

    const save = ({ view, uploadItem }: GtoViewSaveModel) =>
      this.business.updateHousePlanView(
        this.input.id,
        getGraphicalTakeOffHousePlanViewId(view),
        graphicalTakeOffDetailsToHousePlanSaveWithImage(this.form.value, view, uploadItem),
        this.input.entryPoint);

    // upload modified image if necessary
    const uploadings$ = views.map((view) =>
      isGraphicalTakeOffImageTransformed(view) ?
        upload(view).pipe(map$((uploadItem) => <GtoViewSaveModel>({ uploadItem, view })))
        : of$(<GtoViewSaveModel>{ uploadItem: void 0, view }));

    const savings$ = uploadings$.map((uploading$) => uploading$.pipe(switchMap$(save)));

    return from$(savings$).pipe(concatAll$(), last$());
  }

  private loadAllViews(isAfterSave?: boolean): void {
    const { id, entryPoint } = this.input;
    const currentValues = {
      zoom: this.form.zoom,
      scale: this.form.scale,
      currentViewId: this.form.currentViewId,
      currentView: this.form.currentView,
      selectedView: this.form.selectedViewId,
      isOverlay: this.form.value.isOverlay,
      isSnapToPoint: this.form.isSnapToPoint,
    };
    this.business
      .findAllViews(id, entryPoint).pipe(
      switchMap$((views) => this._loadViews(views.map(getGraphicalTakeOffHousePlanViewId))),
      this.waitings.operator())
      .subscribe(
        (views: GraphicalTakeOffViewInfo[]) => {
          this.initViews(views);
          if (isAfterSave) {
            this.openView(getGraphicalTakeOffHousePlanViewId(currentValues.currentView), currentValues);
            return;
          }
          const firstView = first(views);
          if (firstView) {
            this.openView(getGraphicalTakeOffHousePlanViewId(firstView.view));
          } else {
            this.import();
          }
        },
        this.notifications.processAll);
  }

  private initViews(views: GraphicalTakeOffViewInfo[]): void {
    this.form.initViews(this.input, views);
  }

  private loadNewViews(newViews: GraphicalTakeOffHousePlanView[]): void {
    this._loadViews(newViews.map(getGraphicalTakeOffHousePlanViewId)).pipe(
      this.waitings.operator())
      .subscribe((views: GraphicalTakeOffViewInfo[]) => {
        this.form.addViews(views);
        const firstNewView = first(views);
        if (firstNewView) {
          this.openView(getGraphicalTakeOffHousePlanViewId(firstNewView.view));
        }
      });
  }

  private _loadViews(viewIds: ID[]): Observable<GraphicalTakeOffViewInfo[]> {
    if (viewIds.length === 0) {
      return of$([]);
    }

    return combineLatest$(viewIds.map((viewId) => this._loadView(viewId)));
  }

  private _loadView(viewId: ID): Observable<GraphicalTakeOffViewInfo> {
    const { id, entryPoint } = this.input;
    return this.business.findViewData(id, viewId, entryPoint);
  }

  private migrateTopViews(viewId: ID): void {
    const imageSize = this.getImageSize();
    const diagonalLength = this.getImageDiagonal();
    this.form.migrateTopViews(
      viewId,
      // tslint:disable-next-line:no-magic-numbers
      newPoint((imageSize.width - diagonalLength) / 2, (imageSize.height - diagonalLength) / 2));
  }

  private setCurrentViewId(viewId: ID): void {
    this.resetActiveTool();
    this.form.setCurrentViewId(viewId);

    const { currentView } = this.form;

    this.initViewportPosition(currentView);

    const cornerTree: any = new KDBush(
      currentView.corners.map((corner) => this.interpolatePoint(corner)),
      (point: GraphicalTakeOffPoint) => point.x,
      (point: GraphicalTakeOffPoint) => point.y);

    this.cornerIndex = cornerTree;

    // this.redrawSnapGrid(corners);
  }

  private rasterize(view: GraphicalTakeOffHousePlanView): Observable<Blob> {
    return defer$(() => {
      const isCurrentView = getGraphicalTakeOffHousePlanViewId(view) === this.form.currentViewId;
      const sourceRaster = isCurrentView ?
        this.getRaster()
        : createVirtualRaster(getGraphicalTakeOffViewImage(view), getGraphicalTakeOffViewImagePipeline(view));
      // unfortunately typing of rasterize method is invalid
      const raster: Raster = (<any>sourceRaster['rasterize'])(DEFAULT_DPI, false);
      const blob = canvasToBlob(raster.canvas);

      // destroy virtual raster if necessary
      if (!isCurrentView) {
        sourceRaster.remove();
      }
      return blob;
    });
  }

  private setCanvasCursor(cursor: string): void {
    this.scope.view.element.style.cursor = cursor;
  }

  private getCanvasCursor(): string {
    return getComputedStyle(this.scope.view.element).cursor || 'auto';
  }

  private interpolatePoint(point: GraphicalTakeOffPoint): GraphicalTakeOffPoint {
    return point;
    // if (this.form.isSetScaleToolActive) {
    //   // if "scaling" tool is active we should not constraint points by physical coordinates
    //   return point;
    // } else {
    //   const { measurementDefault, picture } = this.form.currentView;
    //   const scaleWidth = picture.scaleWidth || 0;
    //   const scaleHeight = picture.scaleHeight || 0;
    //
    //   // find closest physical point
    //   const physicalPoint = newPoint(point.x * scaleWidth, point.y * scaleHeight);
    //   // round physical point to the minimal meaningful coordinates (0.01 for M and M, for integral IN for FT, etc)
    //   const truncatedPhysicalPoint = truncatePointByUom(physicalPoint, measurementDefault);
    //   // find pixel corresponding to the rounded physical point
    //   return newPoint(
    //     Math.floor(truncatedPhysicalPoint.x / scaleWidth),
    //     Math.floor(truncatedPhysicalPoint.y / scaleHeight));
    // }
  }

  private normalizePoint(point: GraphicalTakeOffPoint,
                         preferredSnapGrid?: GraphicalTakeOffPoint[]): GraphicalTakeOffPoint {
    const interpolatedPoint = this.interpolatePoint(point);

    const snapMaxDistance = GTO_SNAP_GRID_MAX_DISTANCE * this.form.scale;
    if (this.form.isSnapToPoint) {
      if (preferredSnapGrid && preferredSnapGrid.length > 0) {
        const closestPreferredPoint = findClosestPointOnDistance(preferredSnapGrid, interpolatedPoint, snapMaxDistance);
        if (closestPreferredPoint) {
          return closestPreferredPoint;
        }
      }
      const closeCornerIndexes: number[] = this.cornerIndex.within(
        interpolatedPoint.x,
        interpolatedPoint.y,
        snapMaxDistance);
      if (closeCornerIndexes.length > 0) {
        const { corners } = this.form.currentView;
        // tslint:disable-next-line:no-non-null-assertion
        return findClosestPoint(closeCornerIndexes.map((index) => corners[index]), interpolatedPoint)!;
      }
    }

    const { width, height } = this.getImageSize();
    return newPoint(clamp(interpolatedPoint.x, 0, width), clamp(interpolatedPoint.y, 0, height));
  }

  private redrawSnapGrid(corners: GraphicalTakeOffPoint[]): void {
    this.snapGrid.forEach((item) => item.remove());

    this.snapGrid = corners.map(({ x, y }) => {
      // tslint:disable-next-line:no-magic-numbers
      const corner = Shape.Circle(new Point(x, y), 3);
      corner.strokeColor = 'green';
      corner.fillColor = 'green';
      return corner;
    });
  }

  private activateDataBox(targetItem: Item): void {
    const { topId, measurementId, dataBoxId } = targetItem.data;
    if (this.tryToFinalizeMeasurement()) {
      this.selectMeasurement(topId, measurementId);
      this.highlightDataBox(topId, measurementId, dataBoxId);
    }

  }

  private activateMeasurement(targetItem: Item): void {
    if (this.tryToFinalizeMeasurement()) {
      const { topId, measurementId } = targetItem.data;
      this.selectMeasurement(topId, measurementId);
    }
  }

  private initViewportPosition(view: GraphicalTakeOffHousePlanView): void {
    (this.scope.view as any).matrix.reset();
    // tslint:disable-next-line:no-non-null-assertion
    this.setZoom(view.zoom, true, view.viewCenter);
  }

  private getViewportSize(): GraphicalTakeOffSize {
    const { clientWidth, clientHeight } = this.scope.view.element;
    return { width: clientWidth, height: clientHeight };
  }

  private getImageSize(): GraphicalTakeOffSize {
    return getGraphicalTakeOffViewImageSize(this.form.currentView);
  }

  private getImageSiteSize(): GraphicalTakeOffSize {
    if (this.form.isCurrentViewScaled) {
      const { width, height } = this.getImageSize();
      return { width: width + GTO_IMAGE_PADDING_RATIO * width, height: height + GTO_IMAGE_PADDING_RATIO * height };
    } else {
      const diagonal = this.getImageDiagonal();
      return newSize(diagonal, diagonal);
    }
  }

  private getImageDiagonal(): number {
    const imageSize = this.getImageSize();
    return pointLength(newPoint(imageSize.width, imageSize.height));
  }

  private getViewportCenterByScrollPosition(left: number, top: number): GraphicalTakeOffPoint {
    const imageSize = this.getImageSize();
    const imageSiteSize = this.getImageSiteSize();
    const viewportSize = this.getViewportSize();

    return {
      x: viewportSize.width * this.form.scale >= imageSiteSize.width ?
        // tslint:disable-next-line:no-magic-numbers
        imageSize.width / 2
        // tslint:disable-next-line:no-magic-numbers
        : (left + viewportSize.width / 2) * this.form.scale - (imageSiteSize.width - imageSize.width) / 2,
      y: viewportSize.height * this.form.scale >= imageSiteSize.height ?
        // tslint:disable-next-line:no-magic-numbers
        imageSize.height / 2 : (top + viewportSize.height / 2) * this.form.scale - (imageSiteSize.height - imageSize.height) / 2,
    };
  }

  private getScrollPositionByViewportCenter(x: number, y: number): GraphicalTakeOffPoint {
    const imageSize = this.getImageSize();
    const imageSiteSize = this.getImageSiteSize();
    const viewportSize = this.getViewportSize();

    return {
      x: viewportSize.width * this.form.scale >= imageSiteSize.width ?
        0
        // tslint:disable-next-line:no-magic-numbers
        : (x + (imageSiteSize.width - imageSize.width) / 2) / this.form.scale - viewportSize.width / 2,
      y: viewportSize.height * this.form.scale >= imageSiteSize.height ?
        0
        // tslint:disable-next-line:no-magic-numbers
        : (y + (imageSiteSize.height - imageSize.height) / 2) / this.form.scale - viewportSize.height / 2,
    };
  }

  private updateViewportSize(): void {
    const { width, height } = this.getViewportSize();
    const { view } = this.scope;
    view.viewSize.width = width;
    view.viewSize.height = height;
  }

  private adjustViewportPosition(): void {
    // fix view.center position
    const imageSiteSize = this.getImageSiteSize();
    const viewportSize = this.getViewportSize();
    const { scale } = this.form;
    const { view } = this.scope;

    const minCenter = this.getViewportCenterByScrollPosition(0, 0);
    const maxCenter = this.getViewportCenterByScrollPosition(
      imageSiteSize.width / scale - viewportSize.width,
      imageSiteSize.height / scale - viewportSize.height);

    const viewportCenter = newPoint(
      clamp(view.center.x, minCenter.x, maxCenter.x),
      clamp(view.center.y, minCenter.y, maxCenter.y));

    view.center = new Point(viewportCenter.x, viewportCenter.y);

    // fix scroll position
    const scrollPosition = this.getScrollPositionByViewportCenter(viewportCenter.x, viewportCenter.y);
    this.viewportManager.setScrollLeft(scrollPosition.x);
    this.viewportManager.setScrollTop(scrollPosition.y);
  }

  private handleSaveError(error: MessageBasket): void {
    if (error.has('msg_gto_scale_already_set')) {
      this.setViewScale(void 0, void 0);
    }

    this.notifications.processAll(error);
  }

  private rescale(): void {
    this.form.transaction(() => {
      this.form.rescale();
    });
  }

  private saveScale(): void {
    const { currentView, currentViewId } = this.form;

    this.uploadAndSave([currentView]).pipe(
      switchMap$(() => this._loadView(currentViewId)),
      this.waitings.operator())
      .subscribe(
        (view) => {
          this.resetActiveTool();
          this.form.setView(view);
          this.openView(currentViewId);
        },
        (error) => this.handleSaveError(error));
  }

  private setupShortcuts(): void {
    let temporarySnapMode = false;

    const shortcuts: Hash<Hash<GtoShortcutDef[]>> = normalizeShortcutMap({
      [GtoShortcutScope.Global]: {
        'Control': [
          {
            on: 'down',
            when: () => !temporarySnapMode && !this.form.isSnapToPoint && this.form.drawingMode,
            run: () => {
              temporarySnapMode = true;
              this.form.setSnapToPoint(true);
            },
          },
          {
            on: 'up',
            when: () => temporarySnapMode && this.form.drawingMode,
            run: () => {
              temporarySnapMode = false;
              this.form.setSnapToPoint(false);
            },
          },
        ],
      },
      [GtoShortcutScope.Editor]: {
        'Ctrl + Z': [{
          on: 'down',
          when: () => this.form.editable && this.form.drawingMode && this.form.canUndo,
          run: () => this.undo(),
        }],
        'Ctrl + Y': [{
          on: 'down',
          when: () => this.form.editable && this.form.drawingMode && this.form.canRedo,
          run: () => this.redo(),
        }],
        'Delete': [{
          on: 'down',
          when: () => this.form.editable && this.form.drawingMode && isNotNil(this.form.selectedMeasurementId),
          run: () => this.deleteSelectedMeasurement(),
        }],
      },
      [GtoShortcutScope.Canvas]: {},
    });

    this.ngZone.runOutsideAngular(() => {
      // when body is focused (for example, when user clicks on backdrop overlay)
      // we intentionally move focus to the editor
      fromEvent$(document.body, 'focus')
        .pipe(takeUntil$(this.destroy$$))
        .subscribe(() => this.focus(GtoShortcutScope.Editor));

      merge$(...['keydown', 'keyup'].map((type) => fromEvent$(document.body, type)))
        .pipe(takeUntil$(this.destroy$$))
        .subscribe((event: KeyboardEvent) => {
          let el = <Maybe<HTMLElement>>event.target;

          if (el && el.tagName === 'INPUT') {
            // shortcuts should be blocked when event occurred in the input element
            return;
          }

          let scope: Maybe<string>;

          // find target scope of shortcut
          while (el) {
            scope = el.dataset['shortcutScope'];

            if (scope) {
              break;
            }

            el = denull(el.parentElement);
          }
          scope = scope || GtoShortcutScope.Global;

          // find closest shortcut scope having handlers for pressed key
          let handlers: Maybe<GtoShortcutDef[]>;
          do {
            handlers = shortcuts[<GtoShortcutScope>scope][event.key.toLowerCase()];
            scope = getGtoShortcutParentScope(<GtoShortcutScope>scope);
          } while (isNotNil(scope) && isNil(handlers));

          // find suitable handlers and run them
          if (handlers) {
            const targetOn = event.type.substring(3); // tslint:disable-line:no-magic-numbers
            handlers
              .filter(({ on, when }) => targetOn === on && when(event))
              .forEach(({ run }) => {
                this.ngZone.run(() => run(event));
              });
          }
        });
    });
  }

  private selectMeasurement(topId: ID, measurementId: ID): void {
    this.form.selectMeasurement(topId, measurementId);
  }

  private unselectMeasurement(): void {
    this.form.unselectMeasurement();
  }

  private createDrawingTool(): Tool {
    const tool = new Tool();

    const scope = this.scope;

    fromPaperEvent$(tool, 'mousedown').pipe(
      tap$((event: MouseEvent) => {
        const button = event.event.button;
        // tslint:disable-next-line:no-magic-numbers
        if (button === 2) {
          const dialogRef = this.dialogs.openWithInput(graphicalTakeOffZoomDialogRoute, {
            image: getGraphicalTakeOffViewImage(this.form.currentView),
            zoom: this.scope.view.zoom,
            position: fromPaperPoint(event.point),
          });

          dialogRef
            .afterClosed().pipe(
            tap$(() => this.focus(GtoShortcutScope.Canvas)),
            filter$(Boolean))
            .subscribe((point: Point) => {
              this.setMeasurementPoint(point);
            });
        }

        if (button !== 0) {
          return;
        }

        const result = scope.project.hitTest(event.point);
        if (result && result.item) {
          const targetItem = getClosestWithType(result.item);
          if (targetItem) {
            const type = getItemType(targetItem);
            const point = fromPaperPoint(event.point);
            switch (type) {
              case 'databox': {
                event.preventDefault();
                event.stopPropagation();
                if (this.form.shouldDrawPointOverMeasurement(targetItem.data.measurementId)) {
                  this.beginMeasurementPoint(point);
                } else {
                  this.activateDataBox(targetItem);
                }
                break;
              }
              case 'measurement': {
                if (this.form.shouldDrawPointOverMeasurement(targetItem.data.measurementId)) {
                  this.beginMeasurementPoint(point);
                } else {
                  this.activateMeasurement(targetItem);
                }
                break;
              }
              case 'main-layer':
              case 'raster-layer':
                this.beginMeasurementPoint(point);
                break;
              default:
                break;
            }
          }
        }
      }),
      switchMap$(() =>
        fromPaperEvent$(tool, 'mousedrag').pipe(
          takeUntil$(fromPaperEvent$(tool, 'mouseup')),
          finalize$(() => {
            if (this.viewportDragMode) {
              return;
            }

            if (this.form.isMeasurementInProgress) {
              this.finalizeMeasurementPoint();
            } else {
              this.unhighlightDataBox();
              this.form.commitPendingTransaction();
            }
          }))))
      .subscribe((event: MouseEvent) => {
        if (this.viewportDragMode) {
          return;
        }

        const transformedPoint = fromPaperPoint(event.point);
        if (this.form.isMeasurementInProgress) {
          this.setMeasurementPointPosition(transformedPoint);
        } else {
          this.moveSelectedDataBox(transformedPoint);
        }
      });

    this.ngZone.runOutsideAngular(() => {
      fromPaperEvent$(tool, 'mousemove').pipe(
        map$((event: MouseEvent) => {
          if (this.viewportDragMode) {
            // in viewport drag mode => we should not care about cursor
            return void 0;
          }

          const result = scope.project.hitTest(event.point);
          if (result && result.item) {
            const targetItem = getClosestWithType(result.item);
            if (targetItem) {
              const type = getItemType(targetItem);
              switch (type) {
                case 'databox':
                case 'measurement':
                  if (this.form.shouldDrawPointOverMeasurement(targetItem.data.measurementId)) {
                    return 'crosshair';
                  } else {
                    return 'pointer';
                  }
                case 'main-layer':
                case 'raster-layer':
                  return 'crosshair';
                default:
                  break;
              }
            }
          }
          return 'auto';
        }),
        filter$(Boolean),
        map$((cursor) => this.form.viewMode && cursor === 'crosshair' ? 'auto' : cursor),
        )
        .subscribe((cursor: any) => {
          if (cursor) {
            this.setCanvasCursor(cursor);
          }
        });
    });

    return tool;
  }

  private createScalingTool(): Tool {
    const tool = new Tool();

    fromPaperEvent$(tool, 'mousedown').pipe(
      filter$((event: MouseEvent) => event.event.button === 0),
      tap$((event: MouseEvent) => {
        const imageSize = this.getImageSize();
        const point = fromPaperPoint(event.point);

        if (this.form.isScaleLineDrawingInProgress && isPointInBox(point, imageSize)) {
          this.beginScalePoint(fromPaperPoint(event.point));
        }
      }),
      switchMap$(() =>
        fromPaperEvent$(tool, 'mousedrag').pipe(
          takeUntil$(fromPaperEvent$(tool, 'mouseup')),
          finalize$(() => {
            if (this.viewportDragMode) {
              return;
            }

            if (this.form.isScalingInProgress) {
              this.finalizeScalePoint();
            }
          }))))
      .subscribe((event: MouseEvent) => {
        if (this.viewportDragMode) {
          return;
        }

        if (this.form.isScalingInProgress) {
          this.setScalePointPosition(fromPaperPoint(event.point));
        }
      });

    this.ngZone.runOutsideAngular(() => {
      fromPaperEvent$(tool, 'mousemove').pipe(
        map$((event: MouseEvent) => {
          if (this.viewportDragMode) {
            // in viewport drag mode => we should not care about cursor
            return void 0;
          }

          const point = fromPaperPoint(event.point);
          const imageSize = this.getImageSize();

          return isPointInBox(point, imageSize) && this.form.isScaleLineDrawingInProgress ? 'crosshair' : 'grab';
        }),
        filter$(Boolean),
        distinctUntilChanged$())
        .subscribe((cursor: any) => {
          if (cursor) {
            this.setCanvasCursor(cursor);
          }
        });
    });

    return tool;
  }

  private createCropTool(): Tool {
    const tool = new Tool();

    this.ngZone.runOutsideAngular(() => {
      fromPaperEvent$(tool, 'mousemove').pipe(
        map$((event: MouseEvent) => {
          if (this.viewportDragMode) {
            // in viewport drag mode => we should not care about cursor
            return void 0;
          }
          const point = fromPaperPoint(event.point);
          const imageSize = this.getImageSize();

          return isPointInBox(point, imageSize) ? 'crosshair' : 'auto';
        }),
        filter$(Boolean),
        distinctUntilChanged$())
        .subscribe((cursor: any) => {
          if (cursor) {
            this.setCanvasCursor(cursor);
          }
        });
    });

    return tool;
  }

  private createNoopTool(): Tool {
    return new Tool();
  }

  private initTools(): void {
    this.drawingTool = this.createDrawingTool();
    this.scalingTool = this.createScalingTool();
    this.cropTool = this.createCropTool();
    this.noopTool = this.createNoopTool();
  }

  private activateTool(): void {
    // reset cursor
    this.setCanvasCursor('auto');

    if (this.form.editable) {
      if (this.form.drawingMode) {
        this.drawingTool.activate();
      } else if (this.form.isSetScaleToolActive) {
        this.scalingTool.activate();
      } else if (this.form.isCropToolActive) {
        this.cropTool.activate();
      } else {
        this.noopTool.activate();
      }
    } else {
      this.noopTool.activate();
    }
  }

  private beginScalePoint(point: GraphicalTakeOffPoint): void {
    const normalizedPoint = this.normalizePoint(point);
    this.form.beginScalePoint(normalizedPoint);
  }

  private finalizeScalePoint(): void {
    this.form.commitScalePoint();

    const { tools: { scaling: { point1, point2, currentDirection } } } = this.form.value;
    if (point1 && point2) {
      const line = diffPoints(maxPoint(point1, point2), minPoint(point1, point2));

      switch (currentDirection) {
        case GraphicalTakeOffScaleDirection.Horizontal:
          if (!isGtoValidHorizontalScaleLine(line)) {
            this.dialogs.message({
              messageId: 'msg_gto_not_accepted_scale_h',
              severity: MessageSeverity.Error,
            }).subscribe(() => this.form.resetScaleLine());
            return;
          }

          break;
        case GraphicalTakeOffScaleDirection.Vertical:
          if (!isGtoValidVerticalScaleLine(line)) {
            this.dialogs.message({
              messageId: 'msg_gto_not_accepted_scale_v',
              severity: MessageSeverity.Error,
            }).subscribe(() => this.form.resetScaleLine());
            return;
          }

          break;
        default:
          break;
      }

      this.dialogs.message({
        messageId: 'msg_gto_enter_length',
        severity: MessageSeverity.Info,
      }).subscribe(() => {
        this.form.setScaleLineWaitingForLength(true);
      });
    }
  }

  private setScalePointPosition(point: GraphicalTakeOffPoint): void {
    const normalizedPoint = this.normalizePoint(point);
    this.form.setScalePointPosition(normalizedPoint);
  }
}
